import { useRef, useState, useEffect } from 'react';
import { useTr } from '../contexts/LanguageContext';

const useInView = (threshold = 0.08) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
};

const TeamSection = () => {
  const t = useTr();
  const { ref, inView } = useInView();

  const team = [
    {
      id: 'daniel',
      name: 'Daniel Forero',
      title: t('Chief Executive Officer', '최고경영자'),
      image: 'https://customer-assets-wrfwihn1.emergentagent.net/job_swarmcore-hub/artifacts/cnwy8xzq_EABA3069-DA73-490B-83D9-6B670900CF40.png',
      bio: t(
        'Specialization from Harvard and a Wharton specialization in FinTech with a focus on blockchain. Daniel is the protocol\'s technical lead as well as its chief executive: he architected the SwarmBase platform end to end, covering full-stack development, smart contract design, and protocol-level decisions. The core contracts were developed in-house under his direction and independently audited by Hashlock, with all findings resolved.',
        '하버드 전공 및 와튼 스쿨 핀테크 전문화 과정(블록체인 중점)을 이수했습니다. 다니엘은 프로토콜의 기술 총괄이자 최고경영자로서 SwarmBase 플랫폼 전체를 설계했습니다. 풀스택 개발, 스마트 컨트랙트 설계, 프로토콜 수준의 의사결정을 총괄하며, 핵심 컨트랙트는 그의 지휘 하에 자체 개발되었으며 Hashlock의 독립 감사를 통해 모든 지적 사항이 해결되었습니다.'
      ),
      color: '#30d5c8',
    },
    {
      id: 'luke',
      name: 'Luke Nancarrow',
      title: t('Chief Marketing Officer', '최고마케팅책임자'),
      image: 'https://customer-assets-wrfwihn1.emergentagent.net/job_swarmcore-hub/artifacts/si3s0333_A0B44C2C-5C90-47EC-9310-50273A47FD1E.png',
      bio: t(
        'Luke leads brand, community, and go-to-market for SwarmBase, with responsibility for the pre-TGE engagement program, contributor onboarding, and the protocol\'s public communications across all official channels.',
        '루크는 SwarmBase의 브랜드, 커뮤니티, 시장 진입 전략을 총괄합니다. TGE 이전 참여 프로그램 운영, 기여자 온보딩, 모든 공식 채널에서의 프로토콜 대외 커뮤니케이션을 담당하고 있습니다.'
      ),
      color: '#55d532',
    },
    {
      id: 'angela',
      name: 'Angela Davidson',
      title: t('Chief Business Officer', '최고사업책임자'),
      image: 'https://customer-assets-wrfwihn1.emergentagent.net/job_swarmcore-hub/artifacts/zjgfbjjg_E99C54EB-A8CC-4898-ABFD-9816E010FC06.png',
      bio: t(
        'Angela leads business development and partnerships, covering exchange and infrastructure relationships, enterprise adoption of swarm workflows, and the commercial agreements that route real task demand onto the network.',
        '앤젤라는 사업 개발 및 파트너십을 총괄합니다. 거래소 및 인프라 관계, 스웜 워크플로우의 기업 도입, 그리고 네트워크에 실제 작업 수요를 연결하는 상업적 계약을 담당하고 있습니다.'
      ),
      color: '#4de8d0',
    },
    {
      id: 'kim',
      name: 'Kim Sangwook',
      title: t('Head of Korea', '한국 총괄'),
      image: 'https://customer-assets-wrfwihn1.emergentagent.net/job_swarmcore-hub/artifacts/ols0t76l_33196279-D8A0-4970-A6C5-68519091F9D1.png',
      bio: t(
        'Kim Sangwook leads SwarmBase in Korea, covering regional partnerships, institutional relationships, and local contributor growth. His background is in agentic workflow development: decomposing business processes into discrete tasks executed by coordinated AI agents, with defined review checkpoints and measurable output criteria. That work maps directly onto the problem SwarmBase solves at protocol level.',
        '김상욱은 한국에서 SwarmBase를 이끌며, 지역 파트너십, 기관 관계, 현지 기여자 성장을 담당하고 있습니다. 그의 배경은 에이전트 워크플로우 개발에 있습니다. 비즈니스 프로세스를 개별 작업 단위로 분해하여 정의된 검토 체크포인트와 측정 가능한 출력 기준을 갖춘 조율된 AI 에이전트가 실행하도록 합니다. 이러한 작업 방식은 SwarmBase가 프로토콜 수준에서 해결하는 문제와 직결됩니다.'
      ),
      color: '#27d555',
    },
  ];

  return (
    <section id="team" className="py-20 sm:py-24 md:py-32 line-grid"
      style={{ position: 'relative', zIndex: 10, background: 'var(--sb-bg-2)' }}
      data-testid="team-section">
      <div style={{ position: 'absolute', top: '20%', right: '-5%', width: 500, height: 500, background: 'radial-gradient(circle, rgba(48,213,200,0.05) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-10 relative">
        <div ref={ref} className={`mb-12 md:mb-16 anim-hidden ${inView ? 'anim-visible' : ''}`}>
          <div className="section-divider" />
          <p className="text-xs font-mono text-[#30d5c8] tracking-[0.3em] uppercase mb-3">{t('Team', '팀')}</p>
          <h2 className="font-outfit font-black text-white text-3xl sm:text-4xl md:text-5xl tracking-tight">
            {t('The People', 'SwarmBase를')}<br />
            <span className="gradient-text">{t('Building SwarmBase', '만드는 사람들')}</span>
          </h2>
          <p className="text-white/50 mt-4 max-w-xl text-sm md:text-base">
            {t('A core team spanning protocol engineering, growth, business development, and regional expansion.',
               '프로토콜 엔지니어링, 성장, 사업 개발, 지역 확장을 아우르는 핵심 팀입니다.')}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {team.map((member, i) => (
            <div key={member.id} data-testid={`team-card-${member.id}`}
              className={`glass card-3d rounded-2xl overflow-hidden flex flex-col anim-hidden ${inView ? 'anim-visible' : ''}`}
              style={{ transitionDelay: `${i * 0.1}s` }}>
              <div className="relative overflow-hidden" style={{ height: 280 }}>
                <img src={member.image} alt={member.name}
                  className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105" />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '50%', background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)', pointerEvents: 'none' }} />
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-xs font-mono tracking-widest uppercase" style={{ color: member.color }}>{member.title}</p>
                </div>
              </div>
              <div className="p-6 flex flex-col gap-3 flex-1">
                <h3 className="font-outfit font-black text-white text-xl leading-tight">{member.name}</h3>
                <p className="text-white/55 text-xs sm:text-sm leading-relaxed flex-1">{member.bio}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
